import { Recurrence } from '@tableau/self-service-schedules';

import { ToasterService, ToastType } from '~commonInfra/services/ToasterService';
import { ServerApiMapper } from '~commonInfra/utils/ServerModelHelper';
import { CERTIFIED_INDICATOR_TYPE } from '~src/catalog/components/dataQuality/DataQualityType';
import { updateOrDeleteCertificationForItem } from '~src/catalog/restApi/dataQuality/setDataQualityCertificationForItem';
import { Messages } from '~src/codegen/Localize';
import { imgPath } from '~src/commonInfra/utils/ImgUrl';
import { ExtractTasks } from '~src/extractTask/resources/ExtractTasks';
import { CreateExtractTasksWithExtraArgsRequest } from '~src/extractTask/serverApi/requests/CreateExtractTasksWithExtraArgsRequest';
import { SetExtractTasksSelfServiceScheduleRequest } from '~src/extractTask/serverApi/requests/SetExtractTasksSelfServiceScheduleRequest';
import { ExtractTaskTargetType } from '~src/extractTask/serverApi/types/IExtractTask';
import { IDetailFetcher } from '~src/httpClient/fetchers/IDetailFetcher';
import { IListFetcher } from '~src/httpClient/fetchers/IListFetcher';
import { WebClientDetailFetcher } from '~src/httpClient/fetchers/resourceFetchers/WebClientDetailFetcher';
import { WebClientListResourceFetcher } from '~src/httpClient/fetchers/resourceFetchers/WebClientListResourceFetcher';
import { FieldValuesQuery } from '~src/httpClient/resources/FieldValuesQuery';
import { IActionResource, IDetailResource, IFieldValuesResource, IListResource } from '~src/httpClient/resources/Resource';
import { ResourceQuery } from '~src/httpClient/resources/ResourceQuery';
import { GetFieldValuesRequest } from '~src/httpClient/serverApi/requests/GetFieldValuesRequest';
import { IError } from '~src/httpClient/serverApi/types/IError';
import { IFieldValue } from '~src/httpClient/serverApi/types/IFieldValue';
import { IResult } from '~src/httpClient/serverApi/types/IResponse';
import { RequestHelper } from '~src/httpClient/utils/RequestHelper';
import { ResultHelper } from '~src/httpClient/utils/ResultHelper';
import { WebHttpClientWithErrorToastInstance } from '~src/httpClient/WebHttpClientWithErrorToast';
import {
  IPublishedConnectionBase,
  IPublishedConnectionDetailInfo,
  IPublishedConnectionInfo
} from '~src/publishedConnection/models/IPublishedConnectionInfo';
import { PublishedConnectionMapper } from '~src/publishedConnection/models/mappers/PublishedConnectionMapper';
import { AddTagsToPublishedConnectionsRequest } from '~src/publishedConnection/serverApi/requests/AddTagsToPublishedConnectionsRequest';
import {
  CreateDraftFromPublishedConnectionVersionRequest,
  ICreateDraftFromPublishedConnectionVersionResult
} from '~src/publishedConnection/serverApi/requests/CreateDraftFromPublishedConnectionVersionRequest';
import { DeletePublishedConnectionsRequest } from '~src/publishedConnection/serverApi/requests/DeletePublishedConnectionsRequest';
import { GetPublishedConnectionActionsRequest } from '~src/publishedConnection/serverApi/requests/GetPublishedConnectionActionsRequest';
import { GetPublishedConnectionsRequest } from '~src/publishedConnection/serverApi/requests/GetPublishedConnectionsRequest';
import { RemoveTagsFromPublishedConnectionsRequest, } from '~src/publishedConnection/serverApi/requests/RemoveTagsFromPublishedConnectionsRequest';
import { SetExtractTaskPublishedConnectionAttributesRequest } from '~src/publishedConnection/serverApi/requests/SetExtractTaskPconnAttributesRequest';
import { IPublishedConnectionCertificationParams } from '~src/publishedConnection/serverApi/requests/SetPublishedConnectionCertificationRequest';
import {
  IPublishedConnectionDescriptionParams,
  SetPublishedConnectionDescriptionRequest,
} from '~src/publishedConnection/serverApi/requests/SetPublishedConnectionDescriptionRequest';
import { SetPublishedConnectionNameRequest } from '~src/publishedConnection/serverApi/requests/SetPublishedConnectionNameRequest';
import { SetPublishedConnectionsOwnerRequest } from '~src/publishedConnection/serverApi/requests/SetPublishedConnectionsOwnerRequest';
import { IPublishedConnectionActions } from '~src/publishedConnection/serverApi/types/IPublishedConnectionActions';
import {
  MarkRecentlyViewedRequest,
  RecentlyViewedObjectType
} from '~src/recents/requests/MarkRecentlyViewedRequest';
import { ExtractServiceRefreshType } from '~src/schedule/serverApi/requests/CreateSelfServiceScheduleExtractTasksRequest';
import { CreateSelfServiceScheduleExtractTasksWithExtraArgsRequest } from '~src/schedule/serverApi/requests/CreateSelfServiceScheduleExtractTasksWithExtraArgsRequest';
import { SelfServiceSchedule } from '~src/schedule/serverApi/types/SelfServiceSchedule';
import { IScheduleInfo } from '~src/schedule/types/IScheduleInfo';
import { IContentInfo } from '~src/spacesPlatform/models/IContentInfo';
import { ISpaceItemDetailInfo } from '~src/spacesPlatform/models/ISpaceItemDetailInfo';

import WebHttpClient from '../../httpClient/WebHttpClient';
import { DoesActorHaveInflightRevisionForVconnRequest, IDoesActorHaveInflightRevisionForVconnResult } from '../serverApi/requests/DoesActorHaveInflightRevisionForVConnRequest';

class PublishedConnectionsResource implements IListResource<IPublishedConnectionInfo>, IFieldValuesResource, IDetailResource<IPublishedConnectionDetailInfo>, IActionResource<IPublishedConnectionActions> {

  private listFetcher: IListFetcher<IPublishedConnectionInfo> = new WebClientListResourceFetcher(GetPublishedConnectionsRequest, ServerApiMapper.publishedConnections);
  private detailFetcher: IDetailFetcher<IPublishedConnectionDetailInfo> = new WebClientDetailFetcher('getPublishedConnection', PublishedConnectionMapper.mapDetail);
  private fieldValuesFetcher: IListFetcher<IFieldValue> = new WebClientListResourceFetcher(GetFieldValuesRequest, ServerApiMapper.fieldValues);

  public hasExtracts(item: IContentInfo): boolean {
    return this.isPublishedConnectionInfo(item) && !!(item as IPublishedConnectionInfo).hasExtracts;
  }

  public isPublishedConnectionInfo(item: IContentInfo): item is IPublishedConnectionInfo {
    return item &&
      item.contentType &&
      item.contentType === 'virtualConnection';
  }

  public all(): ResourceQuery<IPublishedConnectionInfo> {
    return new ResourceQuery(this.listFetcher);
  }

  public getById(id: string): Promise<IPublishedConnectionDetailInfo> {
    return this.detailFetcher.fetchById(id);
  }

  public valuesForField(field: string): FieldValuesQuery {
    return new FieldValuesQuery(this.fieldValuesFetcher, field, 'virtualConnection');
  }

  // Actions
  public actions(id: string): Promise<IPublishedConnectionActions> {
    return WebHttpClient.sendRequest(new GetPublishedConnectionActionsRequest(id));
  }

  public rename(id: string, name: string): Promise<IResult> {
    return WebHttpClientWithErrorToastInstance.sendRequestAndDelay(new SetPublishedConnectionNameRequest({
      id: id,
      name: name
    }));
  }

  public del(ids: string[]): Promise<IResult> {
    return WebHttpClientWithErrorToastInstance.sendRequestsAndDelay(
      RequestHelper.buildRequests(ids, (idsBlock) => new DeletePublishedConnectionsRequest(idsBlock)),
      ResultHelper.combineResults);
  }

  public setOwner(ids: string[], ownerId: string): Promise<IResult> {
    return WebHttpClientWithErrorToastInstance.sendRequestsAndDelay(
      RequestHelper.buildRequests(ids, (idsBlock) => new SetPublishedConnectionsOwnerRequest(idsBlock, ownerId)),
      ResultHelper.combineResults);
  }

  public addTags(ids: string[], tags: string[]): Promise<IResult> {
    return (tags.length > 0)
      ? WebHttpClientWithErrorToastInstance.sendRequestsAndDelay(
        RequestHelper.buildRequests2(ids, tags,
          (idsBlock, tagsBlock) => new AddTagsToPublishedConnectionsRequest(idsBlock, tagsBlock)),
        ResultHelper.combineResults)
      : Promise.resolve({});
  }

  public removeTags(ids: string[], tags: string[]): Promise<IResult> {
    return (tags.length > 0)
      ? WebHttpClientWithErrorToastInstance.sendRequestsAndDelay(
        RequestHelper.buildRequests2(ids, tags,
          (idsBlock, tagsBlock) => new RemoveTagsFromPublishedConnectionsRequest(idsBlock, tagsBlock)),
        ResultHelper.combineResults)
      : Promise.resolve({});
  }

  public changeTags(ids: string[], tagsToAdd: string[], tagsToRemove: string[]): Promise<IResult> {
    return this.removeTags(ids, tagsToRemove)
      .then((result: IResult) => (ResultHelper.hasErrors(result) ? result : this.addTags(ids, tagsToAdd)));
  }

  public setDescription = async (args: IPublishedConnectionDescriptionParams): Promise<IResult> => {
    return WebHttpClientWithErrorToastInstance
      .sendRequestAndDelay(new SetPublishedConnectionDescriptionRequest(args))
      .then(r => this.toast(r, Messages.editDescriptionSuccessful(), Messages.editDescriptionFailure()));
  };

  public markRecentlyViewed(id: string): Promise<IResult> {
    return WebHttpClient.sendRequest(new MarkRecentlyViewedRequest({
      objectId: id,
      objectType: RecentlyViewedObjectType.PublishedConnection
    }));
  }

  public createExtractTask(id: string, scheduleId: string, tableLuids: ReadonlyArray<string>, extractType: string, level: string): Promise<IResult> {
    return WebHttpClientWithErrorToastInstance.sendRequestAndDelay(
      new CreateExtractTasksWithExtraArgsRequest({
        targetType: ExtractTaskTargetType.PublishedConnection,
        targetIds: [id],
        scheduleId: scheduleId,
        startingItems: [...tableLuids],
        type: extractType,
        level,
      }));
  }

  public updateExtractTask(id: string, scheduleId: string, tableLuids: ReadonlyArray<string>, extractType: string, level: string): Promise<IResult> {
    const pconnAttributesPromise = WebHttpClientWithErrorToastInstance.sendRequestAndDelay(
      new SetExtractTaskPublishedConnectionAttributesRequest({
        id,
        startingItems: [...tableLuids],
        type: extractType,
        level,
      }));
    const schedulePromise = ExtractTasks.setSchedule([id], scheduleId);

    return Promise.all([pconnAttributesPromise, schedulePromise]).then((resultsArray: IResult[]) => {
      const result1Errors = resultsArray[0].errors || [];
      const result2Errors = resultsArray[1].errors || [];
      return {
        errors: [...result1Errors, ...result2Errors]
      };
    });
  }

  public createExtractTaskSelfServiceSchedule(id: number, recurrence: Recurrence, tableLuids: ReadonlyArray<string>, type: ExtractServiceRefreshType, level: string): Promise<IResult> {
    return WebHttpClientWithErrorToastInstance.sendRequestAndDelay(
      new CreateSelfServiceScheduleExtractTasksWithExtraArgsRequest({
        targetType: ExtractTaskTargetType.PublishedConnection,
        targetIds: [id],
        schedule: SelfServiceSchedule.fromRecurrence(recurrence, 'Extract'),
        scheduleName: IScheduleInfo.getRecurrenceDescription(recurrence).substring(0, 255),
        startingItems: [...tableLuids],
        type,
        level,
      }));
  }

  public updateExtractTaskSelfServiceSchedule(id: number, recurrence: Recurrence, tableLuids: ReadonlyArray<string>, type: ExtractServiceRefreshType, level: string): Promise<IResult> {
    const pconnAttributesPromise = WebHttpClientWithErrorToastInstance.sendRequestAndDelay(
      new SetExtractTaskPublishedConnectionAttributesRequest({
        id: id.toString(),
        startingItems: [...tableLuids],
        type: type as string,
        level,
      }));
    const schedulePromise = this.setExtractTasksSelfServiceSchedule([id], recurrence, type);

    return Promise.all([pconnAttributesPromise, schedulePromise]).then((resultsArray: IResult[]) => {
      const result1Errors = resultsArray[0].errors || [];
      const result2Errors = resultsArray[1].errors || [];
      return {
        errors: [...result1Errors, ...result2Errors]
      };
    });
  }

  public setExtractTasksSelfServiceSchedule(ids: number[], recurrence: Recurrence, type: ExtractServiceRefreshType) {
    return WebHttpClientWithErrorToastInstance.sendRequestAndDelay(
      new SetExtractTasksSelfServiceScheduleRequest({
        schedule: SelfServiceSchedule.fromRecurrence(recurrence, 'Extract'),
        scheduleName: IScheduleInfo.getRecurrenceDescription(recurrence).substring(0, 255),
        type,
        ids,
      }));
  }

  public setCertification(params: IPublishedConnectionCertificationParams & { luid?: string }): Promise<IResult> {
    return updateOrDeleteCertificationForItem({ ...params, contentType: 'virtualConnection' });
  }

  public hasDataQualityWarning(contentItem: IPublishedConnectionBase): boolean {
    return contentItem.dataQualityIndicators
      && contentItem.dataQualityIndicators[0]
      && contentItem.dataQualityIndicators[0].isActive;
  }

  public hasCertification(contentItem: IPublishedConnectionBase): boolean {
    return contentItem.dataQualityIndicators
      && contentItem.dataQualityIndicators.some(dqi => dqi.isActive && dqi.type === CERTIFIED_INDICATOR_TYPE);
  }

  // Toast
  public toast(result: IResult, successMessage: string, failureMessage?: string): any {
    if (result.errors && result.errors.length > 0) {
      if (failureMessage) {
        ToasterService.getInstance().pop({
          type: ToastType.Error,
          text: failureMessage
        });
      } else {
        result.errors.forEach((error: IError) => {
          ToasterService.getInstance().pop({
            type: ToastType.Error,
            text: error.message
          });
        });
      }
    } else {
      ToasterService.instance.pop({
        type: ToastType.Success,
        text: successMessage
      });
    }
  }

  public getPublishedConnectionIconPath(contentInfo: ISpaceItemDetailInfo<'virtualConnection'>, headerIcon: boolean = true): string {
    const properties = [];
    if (contentInfo.hasExtracts) {
      properties.push('extract');
    }
    if (properties.length > 0) {
      properties.push('');
    }

    if (headerIcon) {
      return imgPath(`server_connection_${properties.join('_')}60px_card.svg`);
    } else {
      return imgPath(`server_connection_${properties.join('_')}16px_medium.svg`);
    }
  }

  public createDraftFromPublishedConnectionVersion(id: string, version: number): Promise<ICreateDraftFromPublishedConnectionVersionResult> {
    const request = new CreateDraftFromPublishedConnectionVersionRequest({
      id: id,
      version: version
    });
    return WebHttpClientWithErrorToastInstance.sendRequestAndDelay(request);
  }

  public doesUserHaveInflightRevision(id: string): Promise<IDoesActorHaveInflightRevisionForVconnResult>{
    const request = new DoesActorHaveInflightRevisionForVconnRequest({
      id: id
    });
    return WebHttpClient.sendRequest(request);
  }
}

export const PublishedConnections = new PublishedConnectionsResource();
